import { IUser, ResourceAutoCompleteViewModelType } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { isValidEmail } from '../../../../models/UiUtils';
import { ResourceAutoCompleteViewModel } from '../../../../viewmodels/AppViewModels';
import { Pill } from '../../../components-v2/Pill/Pill';
import { Avatar } from '@HoudiniUI/Avatar';
import { getAvatarProps } from '@AppModels/AvatarUtils';
import { AutoCompleteDropdown, IAutoCompleteDropdownComponentProps } from '../../autocomplete/AutoCompleteDropdown';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	disabled?: boolean;
	onEmployeesChange?(employees: IUser[]): void;
	employees?: IUser[];
	placeholder?: string;
}

const _MultiEmployeeSelector: React.FC<IProps> = props => {
	const {
		className,
		disabled,
		onEmployeesChange,
		employees: propsEmployees,
		placeholder,
		impersonationContext,
	} = props;
	const userSession = useUserSession();

	// State
	const [inputHasFocus, setInputHasFocus] = useState<boolean>(false);
	const [employees, setEmployees] = useState<IUser[]>(propsEmployees || []);

	// Refs
	const dropdownElementRef = useRef<typeof AutoCompleteDropdown>(null);
	const scrollToBottomAnchorRef = useRef<HTMLSpanElement>(null);
	const processingEmailRef = useRef<string | null>(null);

	// Initialize searchViewModel - equivalent to UNSAFE_componentWillMount
	const searchViewModel = useMemo(() => {
		const vm = new ResourceAutoCompleteViewModel<IUser>(userSession, {
			filter: (x: IUser) => {
				const emailAddress = x.primaryEmail;
				return !!emailAddress && !!emailAddress.value;
			},
			pageSize: 25,
			type: ResourceAutoCompleteViewModelType.User,
		});
		vm.impersonate(impersonationContext);
		return vm;
	}, [userSession, impersonationContext]);

	// Handle props.employees changes - equivalent to UNSAFE_componentWillReceiveProps
	useEffect(() => {
		setEmployees(propsEmployees || []);
	}, [propsEmployees]); // Only run when propsEmployees changes

	const onScrollToBottomAnchorRef = useCallback((ref?: HTMLSpanElement) => {
		scrollToBottomAnchorRef.current = ref;
	}, []);

	const onTransformSearchResults = useCallback((searchResults?: IUser[]): IUser[] => {
		return (searchResults || []).map(x => ({
			...x,
		}));
	}, []);

	const resetDropdownInputContent = useCallback(() => {
		if (dropdownElementRef.current) {
			(dropdownElementRef.current as any).reset();
		}
	}, []);

	const scrollToBottom = useCallback(() => {
		setTimeout(() => {
			if (scrollToBottomAnchorRef.current) {
				scrollToBottomAnchorRef.current.scrollIntoView();
			}
		}, 1);
	}, []);

	const focus = useCallback(() => {
		if (dropdownElementRef.current && !disabled) {
			(dropdownElementRef.current as any).focus();
			scrollToBottom();
		}
	}, [disabled, scrollToBottom]);

	const addTokenForEmail = useCallback(
		(email: string) => {
			// Prevent double execution within the same render cycle
			if (processingEmailRef.current === email) {
				return;
			}

			processingEmailRef.current = email;

			const employee: IUser = {
				id: `non-employee-${email}`, // Generate unique ID for non-employee emails
				email,
				primaryEmail: {
					value: email,
				},
			};
			const existingEmployees = employees || [];
			const alreadyExists = existingEmployees.some(emp => emp.id === employee.id || emp.email === employee.email);

			if (!alreadyExists) {
				const newEmployees = [...existingEmployees, employee];

				if (onEmployeesChange) {
					onEmployeesChange(newEmployees);
				} else {
					setEmployees(newEmployees);
				}

				// Clear the input immediately after adding the token
				resetDropdownInputContent();

				// Focus after a short delay to allow the reset to complete
				setTimeout(() => {
					focus();
					// Reset the processing flag after everything is done
					processingEmailRef.current = null;
				}, 50);
			} else {
				processingEmailRef.current = null;
			}
		},
		[employees, onEmployeesChange, focus, resetDropdownInputContent]
	);

	const onInputKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			const text = (e.target as HTMLInputElement).value;

			if (['Enter', ',', ';', 'Tab'].includes(e.key)) {
				// Check if the capture handler already processed this
				if (e.defaultPrevented) {
					return;
				}

				// Only handle invalid emails or empty inputs - valid emails are handled by capture
				if (text && !isValidEmail(text)) {
					resetDropdownInputContent();
					setTimeout(() => {
						focus();
					});
				}
			}
		},
		[resetDropdownInputContent, focus]
	);

	const onToggleInputFocus = useCallback(
		(inputHasFocusValue: boolean) => (e: React.FocusEvent<HTMLInputElement>) => {
			if (!inputHasFocusValue) {
				const text = (e.target as HTMLInputElement).value;
				if (!!text && isValidEmail(text)) {
					// create the token
					addTokenForEmail(text);
				}
			}
			setInputHasFocus(inputHasFocusValue);
		},
		[addTokenForEmail]
	);

	const onAutoCompleteDropdownRef = useCallback((ref: any) => {
		dropdownElementRef.current = ref;
	}, []);

	const onRemoveEmployee = useCallback(
		(employee: IUser) => {
			const employeesArray = [...(employees || [])];
			const index = employeesArray.findIndex(emp => emp.id === employee.id || emp.email === employee.email);
			if (index >= 0) {
				employeesArray.splice(index, 1);
				if (onEmployeesChange) {
					onEmployeesChange(employeesArray);
				} else {
					setEmployees(employeesArray);
				}
			}
		},
		[employees, onEmployeesChange]
	);

	const onRequestDeleteBackwards = useCallback(() => {
		// Remove last employee when backspace is pressed on empty input
		if (employees && employees.length > 0) {
			const lastEmployee = employees[employees.length - 1];
			onRemoveEmployee(lastEmployee);
		}
	}, [employees, onRemoveEmployee]);

	const onRenderEmptyResults = useCallback(() => {
		return (
			<div className={css(styleSheet.multiEmployeeSelectorNoMatches)}>
				No matching employee found. Press Enter, Tab, comma, or semicolon to add as email contact.
			</div>
		);
	}, []);

	const onEmployeeSelected = useCallback(
		(employee: IUser) => {
			// Check if employee already exists to avoid duplicates
			const existingEmployees = employees || [];
			const alreadyExists = existingEmployees.some(emp => emp.id === employee.id || emp.email === employee.email);

			if (!alreadyExists) {
				const newEmployees = [...existingEmployees, employee];
				if (onEmployeesChange) {
					onEmployeesChange(newEmployees);
				} else {
					setEmployees(newEmployees);
				}
			}
		},
		[employees, onEmployeesChange]
	);

	const onEmployeeClicked = useCallback(
		(employee: IUser) => (e: React.MouseEvent<HTMLElement>) => {
			e.preventDefault();
			e.stopPropagation();

			if (employees && !employees.some(x => x.id === employee.id || x.email === employee.email)) {
				onEmployeeSelected(employee);
			}
			resetDropdownInputContent();
		},
		[employees, onEmployeeSelected, resetDropdownInputContent]
	);

	const onAutoCompleteItemRender = useCallback(
		(employee: IUser, index: number, highlighted: boolean) => {
			const displayName = getDisplayName(employee);
			return (
				<div
					className={css(
						styleSheet.multiEmployeeSelectorEmployee,
						highlighted && styleSheet.multiEmployeeSelectorEmployeeHighlighted
					)}
					key={employee.id || employee.email || index}
					onMouseDown={onEmployeeClicked(employee)}
				>
					<Avatar {...getAvatarProps(employee)} styles={[styleSheet.multiEmployeeSelectorEmployeeAvatar]} />
					<div className={css(styleSheet.multiEmployeeSelectorEmployeeDetails)}>
						<div className={css(styleSheet.multiEmployeeSelectorEmployeeDetailsName)} title={displayName}>
							{displayName}
						</div>
						{!!employee.primaryEmail?.value && (
							<div
								className={css(styleSheet.multiEmployeeSelectorEmployeeDetailsEmail)}
								title={employee.primaryEmail.value}
							>
								{employee.primaryEmail.value}
							</div>
						)}
					</div>
				</div>
			);
		},
		[onEmployeeClicked]
	);

	const onTokenClicked = useCallback((e: React.MouseEvent<HTMLElement>) => {
		// prevent the input box from showing
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const onTokenContainerClicked = useCallback(() => {
		focus();
	}, [focus]);

	const anchorProvider = useCallback(
		(inputElement: React.ReactElement<any>) => {
			return (
				<div
					className={`${css(styleSheet.multiEmployeeSelector, disabled && styleSheet.multiEmployeeSelectorDisabled)} ${className || ''}`}
					onKeyDownCapture={(e: React.KeyboardEvent) => {
						// Use capture phase to intercept events BEFORE they reach any child components
						const target = e.target as HTMLInputElement;
						const text = target?.value || '';

						if (['Enter', ',', ';', 'Tab'].includes(e.key)) {
							if (text && isValidEmail(text)) {
								e.preventDefault();
								e.stopPropagation();
								e.nativeEvent.stopImmediatePropagation();
								addTokenForEmail(text);
								return;
							}
						}
					}}
				>
					<div
						className={css(
							styleSheet.multiEmployeeSelectorContent,
							disabled && styleSheet.multiEmployeeSelectorContentDisabled,
							inputHasFocus && styleSheet.multiEmployeeSelectorContentFocus
						)}
						onClick={!disabled ? onTokenContainerClicked : undefined}
					>
						<div className={css(styleSheet.multiEmployeeSelectorTokens)}>
							{!!employees &&
								employees.map((employee, i) => {
									return (
										<div
											className={css(styleSheet.multiEmployeeSelectorToken)}
											key={employee.id || employee.email || i}
											onClick={onTokenClicked}
										>
											<Pill
												label={getDisplayName(employee)}
												variant='tag'
												color='blue'
												disabled={disabled}
												onRemove={!disabled ? () => onRemoveEmployee(employee) : undefined}
											/>
										</div>
									);
								})}
							{!disabled && inputElement}
							<span ref={onScrollToBottomAnchorRef} />
						</div>
					</div>
				</div>
			);
		},
		[
			className,
			disabled,
			inputHasFocus,
			employees,
			onTokenContainerClicked,
			onTokenClicked,
			onRemoveEmployee,
			onScrollToBottomAnchorRef,
			addTokenForEmail,
		]
	);

	const autocompleteProps: Partial<IAutoCompleteDropdownComponentProps<IUser, IUser>> = {
		searchResultsTransformer: onTransformSearchResults,
	};

	return (
		<AutoCompleteDropdown
			{...autocompleteProps}
			anchor={anchorProvider}
			autoCompleteViewModel={searchViewModel}
			className={css(styleSheet.multiEmployeeSelectorDropdown)}
			dropdownContentClassName={css(styleSheet.multiEmployeeSelectorDropdownContent)}
			onItemSelected={onEmployeeSelected}
			onRenderEmptyResults={onRenderEmptyResults}
			onRenderItem={onAutoCompleteItemRender}
			onRequestDeleteBackwards={onRequestDeleteBackwards}
			ref={onAutoCompleteDropdownRef}
			popoverStyles={[styleSheet.multiEmployeeSelectorPopover]}
			inputProps={{
				className: css(styleSheet.multiEmployeeSelectorTokenContainerInput),
				disabled,
				onBlur: onToggleInputFocus(false),
				onFocus: onToggleInputFocus(true),
				onKeyDown: onInputKeyDown,
				placeholder: placeholder || 'Search for Employees',
				style: {
					opacity: inputHasFocus ? 1 : 0,
				},
			}}
		/>
	);
};

const MultiEmployeeSelectorAsObserver = observer(_MultiEmployeeSelector);
export const MultiEmployeeSelector = inject(ImpersonationContextKey)(MultiEmployeeSelectorAsObserver);
